<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div v-if="isSuccess" class="full-height">
        <div v-if="items.length" class="table">
          <div class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber }}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong>&nbsp;<span class="text--lowercase">{{ $t(`${namespace}.lines`) }}</span>
          </div>

          <v-layout class="table__header" align-center>
            <v-flex class="table__checkbox" style="max-width: 40px;" xs1 />
            <v-flex xs3>{{ $t(`${namespace}.requester`) }}</v-flex>
            <v-flex xs2>{{ $t(`${namespace}.createdDate`) }}</v-flex>
            <v-flex xs2>{{ $t(`${namespace}.table.workerStartedDate`) }}</v-flex>
            <v-flex xs4>{{ $t(`${namespace}.status`) }}</v-flex>
          </v-layout>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile class="table__body__tr">
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex class="table__checkbox table__td--prevent" style="max-width: 40px;" xs1>
                        <v-menu
                          v-if="isValidArray(item.success) || isValidArray(item.uploadErrors)"
                          offset-y
                          right
                        >
                          <button class="table-body__actions" slot="activator">
                            <v-icon>
                              more_vertical
                            </v-icon>
                          </button>

                          <v-list class="dropdown-menu-actions__list">
                            <v-list-tile
                              v-if="isValidArray(item.success)"
                              @click="openPreviewImportedLinesDialog('success', item.success)"
                            >
                              <v-list-tile-title>{{$t(`${namespace}.linesImportedWithSuccess`)}}</v-list-tile-title>
                            </v-list-tile>

                            <v-list-tile
                              v-if="isValidArray(item.uploadErrors)"
                              @click="openPreviewImportedLinesDialog('error', item.uploadErrors)"
                            >
                              <v-list-tile-title>{{$t(`${namespace}.linesImportedWithError`)}}</v-list-tile-title>
                            </v-list-tile>

                            <v-list-tile
                              v-if="isValidArray(item.workerErrors)"
                              @click="openPreviewImportedLinesDialog('workerErrors', item.workerErrors)"
                            >
                              <v-list-tile-title>{{$t(`${namespace}.workerErrors`)}}</v-list-tile-title>
                            </v-list-tile>
                          </v-list>
                        </v-menu>
                      </v-flex>

                      <v-flex xs3>
                        <span class="text--ellipsis text--capitalize" title="">
                          {{ item.username }}
                        </span>
                        <span class="gray-6--text caption text--ellipsis">{{ item.useremail }}</span>
                      </v-flex>

                      <v-flex xs2>
                        <span class="text--ellipsis">
                          {{ moment(item.createdDate).format('DD/MM/YYYY') }}
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span class="text--ellipsis">
                          {{ moment(item.workerStartedDate).format('DD/MM/YYYY HH:mm') }}
                        </span>
                      </v-flex>

                      <v-flex xs4>
                        <StatusBullet
                          :status="getStatusColor(item.status)"
                          :label="getStatusText(item.status)"
                        />
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="employee"
          :text="$t('Common.emptyFinancial')"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          style="margin-bottom: 120px"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>

    <PreviewImportedLinesDialog
      :visible="previewImportedLinesDialog.visible"
      :type="previewImportedLinesDialog.type"
      :data="isValidArray(previewImportedLinesDialog.data) ? previewImportedLinesDialog.data : []"
      @close="closePreviewImportedLinesDialog"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { formatNumber, objectToArray, isArray } from 'utils/functions'
import EmptyState from 'components/EmptyState'
import StatusBullet from 'components/company/StatusBullet'
import PreviewImportedLinesDialog from './PreviewImportedLinesDialog'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    },
    drawerVisibility: {
      type: Boolean
    }
  },
  components: {
    EmptyState,
    StatusBullet,
    PreviewImportedLinesDialog
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany
    }),

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.PaymentStatusPage',
      previewImportedLinesDialog: {
        data: null,
        visible: false,
        type: null
      }
    }
  },
  methods: {
    isValidArray (val) {
      return isArray(val)
    },

    getStatusList (status) {
      return status
    },

    handlePage (page) {
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    openPreviewImportedLinesDialog (type, data) {
      this.previewImportedLinesDialog = {
        data,
        visible: true,
        type
      }
    },

    closePreviewImportedLinesDialog (type, item) {
      this.previewImportedLinesDialog = {
        data: null,
        visible: false,
        type: null
      }
    },

    getBilletType (type) {
      const types = {
        'BOLETO': this.$t('Components.CompanyFinancialConfig.bankBillet'),
        'CREDIT_CARD': this.$t('Components.CompanyFinancialConfig.creditCard'),
        'BANK_TRANSFER': this.$t('Components.CompanyFinancialConfig.bankTransfer')
      }

      return types[type]
    },

    getStatusText (status) {
      const statusTarget = objectToArray(this.$t('Views.PaymentStatusPage.filters.importedSpreadsheetsStatus')).find(item => item.value === status)
      return statusTarget ? statusTarget.text : status
    },

    getStatusColor (status) {
      const colors = {
        UPLOADED: 'late',
        STARTED: 'late',
        FINISHED: 'success',
        FINISHED_WITH_ERRORS: 'authorised',
        ERROR: 'deleted'
      }

      return colors[status]
    }
  }
}
</script>
